import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface DiceProps {
  value: number | null;
  isRolling: boolean;
  onRoll: () => void;
  disabled?: boolean;
}

const Dice: React.FC<DiceProps> = ({ value, isRolling, onRoll, disabled = false }) => {
  const displayValue = value || 1;
  
  const getDiceFace = (num: number) => {
    const dots: React.ReactNode[] = [];
    const positions: Record<number, string[]> = {
      1: ['center'],
      2: ['top-right', 'bottom-left'],
      3: ['top-right', 'center', 'bottom-left'],
      4: ['top-left', 'top-right', 'bottom-left', 'bottom-right'],
      5: ['top-left', 'top-right', 'center', 'bottom-left', 'bottom-right'],
      6: ['top-left', 'top-right', 'middle-left', 'middle-right', 'bottom-left', 'bottom-right'],
    };
    
    return positions[num] || [];
  };
  
  const dotPositions: Record<string, string> = {
    'top-left': 'top-2 left-2',
    'top-right': 'top-2 right-2',
    'middle-left': 'top-1/2 left-2 -translate-y-1/2',
    'middle-right': 'top-1/2 right-2 -translate-y-1/2',
    'center': 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
    'bottom-left': 'bottom-2 left-2',
    'bottom-right': 'bottom-2 right-2',
  };
  
  const activePositions = getDiceFace(displayValue);
  
  return (
    <div className="flex flex-col items-center gap-4">
      <motion.button
        onClick={onRoll}
        disabled={disabled || isRolling}
        className={`
          relative w-32 h-32 rounded-2xl
          ${disabled || isRolling 
            ? 'bg-gray-700 cursor-not-allowed opacity-50' 
            : 'bg-gradient-to-br from-amber-400 to-amber-600 cursor-pointer hover:from-amber-300 hover:to-amber-500'
          }
          shadow-2xl shadow-amber-500/30
          transition-all duration-200
          flex items-center justify-center
          border-4 border-amber-300
        `}
        animate={isRolling ? {
          rotate: [0, 10, -10, 10, -10, 0],
          scale: [1, 1.1, 0.9, 1.1, 0.9, 1],
        } : {}}
        transition={isRolling ? {
          duration: 0.3,
          repeat: Infinity,
        } : {}}
        whileHover={!disabled && !isRolling ? { scale: 1.05 } : {}}
        whileTap={!disabled && !isRolling ? { scale: 0.95 } : {}}
      >
        <div className="relative w-24 h-24">
          <div
            className="absolute inset-0 flex items-center justify-center"
            key={`dice-face-${displayValue}`}
          >
            <div className="relative w-full h-full">
              {activePositions.map((pos, i) => (
                <div
                  key={`${pos}-${i}`}
                  className={`absolute w-5 h-5 bg-gray-900 rounded-full shadow-inner ${dotPositions[pos]}`}
                />
              ))}
            </div>
          </div>
        </div>
        
        {/* Rolling effect overlay */}
        {isRolling && (
          <motion.div
            className="absolute inset-0 bg-amber-400/20 rounded-2xl"
            animate={{ opacity: [0.2, 0.5, 0.2] }}
            transition={{ duration: 0.2, repeat: Infinity }}
          />
        )}
      </motion.button>
      
      {!disabled && !isRolling && (
        <motion.p
          className="text-amber-300 font-bold text-lg uppercase tracking-wider"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          Tap to Roll!
        </motion.p>
      )}
      
      {isRolling && (
        <motion.p
          className="text-amber-400 font-bold text-lg"
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 0.3, repeat: Infinity }}
        >
          Rolling...
        </motion.p>
      )}
      
      {disabled && !isRolling && value && (
        <motion.div
          className="text-center"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <p className="text-amber-300 text-lg font-bold">Rolled: {value}</p>
          <p className="text-gray-400 text-sm">Move {value} spaces • Take {value} sips</p>
        </motion.div>
      )}
    </div>
  );
};

export default Dice;
